/**
 * offlinePhotoStore.ts
 * Persiste les photos ET les brouillons EDL dans IndexedDB.
 *
 * @version 5.0.0
 *   - v3.2.0 : + getAllGroups() pour PendingQueueScreen
 *   - v4.0.0 : + saveDraft / getDraft / clearDraft
 *              (logique migrée depuis useEdlDraftStore.ts)
 *              useEdlDraftStore devient un simple re-export de compatibilité
 *   - v5.0.0 : Migration inspectionId → inspectionKey (dep-{numero} / retour-{numero})
 *              Toutes les opérations utilisent désormais inspectionKey comme clé primaire
 *              pour éliminer les bugs d'attribution photo ↔ contrat.
 */

import { getDB } from './idb'

// ─────────────────────────────────────────────────────────────────────────────
// Types — Photos
// ─────────────────────────────────────────────────────────────────────────────

export type InspectionType = 'depart' | 'retour'

export interface PhotoEntry {
  dataUrl:         string
  originalDataUrl: string
}

export type PhotoMap = Record<string, PhotoEntry>

interface StoredPhoto extends PhotoEntry {
  key:            string
  inspectionId:   string       // ✅ v5.0 — désormais = inspectionKey (ex: "dep-CD25-04567")
  type:           InspectionType
  zone:           string
  savedAt:        string
}

export interface PhotoGroup {
  contractId: string
  type:       InspectionType
  count:      number
}

// ─────────────────────────────────────────────────────────────────────────────
// Types — Drafts (identiques à useEdlDraftStore pour compatibilité)
// ─────────────────────────────────────────────────────────────────────────────

export interface EdlDraft {
  key:        string
  contractId: string
  type:       'depart' | 'retour'
  plate?:     string | null
  mileage:    number | null
  fuel:       number | null
  damages:    unknown[]
  contract:   unknown
  startedAt:  string | null
  savedAt:    string
  step:       string
  // Champs Phase Sync v4.0
  payload?:   unknown   // payload complet si saveImmediate
}

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const PHOTOS_STORE          = 'pending-photos'
const DRAFTS_STORE          = 'edl-drafts'
const DEPARTURE_CACHE_STORE = 'departure-cache'

const QUOTA_WARN_RATIO          = 0.85
const DEPARTURE_CACHE_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000  // 7 jours

// ─────────────────────────────────────────────────────────────────────────────
// Helpers IDB
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Vérifie le quota de stockage IndexedDB.
 * Retourne un objet structuré exploitable par l'UI pour afficher des alertes.
 */
export async function checkStorageQuota(): Promise<{
  used: number
  quota: number
  percentUsed: number
  isNearLimit: boolean
}> {
  if (!navigator.storage?.estimate) {
    return { used: 0, quota: 0, percentUsed: 0, isNearLimit: false }
  }
  try {
    const { usage = 0, quota = 1 } = await navigator.storage.estimate()
    const percentUsed = quota > 0 ? (usage / quota) * 100 : 0
    const isNearLimit = quota > 0 && (usage / quota) > QUOTA_WARN_RATIO
    if (isNearLimit) {
      console.warn(
        `[offlinePhotoStore] ⚠️ Stockage à ${Math.round(percentUsed)}% — ` +
        `${Math.round((quota - usage) / 1024 / 1024)}MB restants`
      )
    }
    return { used: usage, quota, percentUsed, isNearLimit }
  } catch {
    return { used: 0, quota: 0, percentUsed: 0, isNearLimit: false }
  }
}

function txComplete(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve()
    tx.onerror    = () => reject(new Error(`[IndexedDB] Transaction échouée: ${tx.error?.message}`))
    tx.onabort    = () => reject(new Error('[IndexedDB] Transaction annulée'))
  })
}

function idbRequest<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result)
    req.onerror   = () => reject(new Error(`[IndexedDB] Requête échouée: ${req.error?.message}`))
  })
}

/**
 * Construit la clé de brouillon.
 * ✅ v5.0 — Accepte inspectionKey directement (ex: "dep-CD25-04567")
 *           OU contractId + type en fallback legacy.
 */
function makeDraftKey(inspectionKeyOrContractId: string, type?: 'depart' | 'retour'): string {
  const keyStr = String(inspectionKeyOrContractId)
  // Si la valeur est déjà un inspectionKey (contient dep- ou retour-), l'utiliser directement
  if (keyStr.startsWith('dep-') || keyStr.startsWith('retour-')) {
    return keyStr
  }
  // Fallback legacy : contractId + type
  if (!type) {
    console.warn('[offlinePhotoStore] makeDraftKey appelé sans type et sans inspectionKey — clé potentiellement ambiguë')
    return keyStr
  }
  return `${keyStr}::${type}`
}

// ─────────────────────────────────────────────────────────────────────────────
// API publique
// ─────────────────────────────────────────────────────────────────────────────

export const offlinePhotoStore = {

  // ── Photos ────────────────────────────────────────────────────────────────

  /**
   * Sauvegarde des photos dans IndexedDB.
   * ✅ v5.0 — inspectionKey = "dep-CD25-04567" ou "retour-CD25-04567"
   *           Le type est déduit du key, mais passé en paramètre pour compatibilité.
   */
  async savePhotos(
    inspectionKey: string,
    photos:        Record<string, PhotoEntry | string | null | undefined>,
    type:          InspectionType = 'retour'
  ): Promise<void> {
    if (!photos || !Object.keys(photos).length) return
    if (!inspectionKey) {
      console.error('[offlinePhotoStore] ❌ savePhotos appelé sans inspectionKey — opération annulée')
      return
    }
    const quotaInfo = await checkStorageQuota()
    if (quotaInfo.isNearLimit) {
      console.warn('[offlinePhotoStore] ⚠️ Stockage proche de la limite — sauvegarde quand même')
    }

    const keyStr = String(inspectionKey)
    const db = await getDB()
    const tx = db.transaction(PHOTOS_STORE, 'readwrite')
    const st = tx.objectStore(PHOTOS_STORE)

    for (const [zone, photoData] of Object.entries(photos)) {
      if (!photoData) continue
      const dataUrl = typeof photoData === 'string' ? photoData : photoData.dataUrl ?? null
      if (!dataUrl) continue
      const originalDataUrl = typeof photoData === 'string'
        ? photoData
        : photoData.originalDataUrl ?? dataUrl

      st.put({
        key: `${keyStr}_${zone}`,
        inspectionId: keyStr,  // ✅ v5.0 — inspectionKey comme clé d'index
        type, zone, dataUrl, originalDataUrl,
        savedAt: new Date().toISOString(),
      } satisfies StoredPhoto)
    }

    await txComplete(tx)
    console.log(`[offlinePhotoStore] ✅ ${Object.keys(photos).length} photos sauvegardées — ${inspectionKey}`)
  },

  /**
   * Récupère les photos depuis IndexedDB.
   * ✅ v5.0 — inspectionKey = "dep-CD25-04567" ou "retour-CD25-04567"
   */
  async getPhotos(
    inspectionKey: string,
    type?:         InspectionType
  ): Promise<PhotoMap> {
    const keyStr = String(inspectionKey)
    const db    = await getDB()
    const tx    = db.transaction(PHOTOS_STORE, 'readonly')
    const index = tx.objectStore(PHOTOS_STORE).index('inspectionId')

    const [all] = await Promise.all([
      idbRequest<StoredPhoto[]>(index.getAll(keyStr)),
      txComplete(tx),
    ])

    const filtered = type ? all.filter(p => p.type === type) : all
    return Object.fromEntries(
      filtered.map(p => [p.zone, { dataUrl: p.dataUrl, originalDataUrl: p.originalDataUrl }])
    )
  },

  /**
   * Supprime les photos d'une inspection.
   * ✅ v5.0 — inspectionKey = "dep-CD25-04567" ou "retour-CD25-04567"
   */
  async clearPhotos(
    inspectionKey: string,
    type?:         InspectionType
  ): Promise<void> {
    const keyStr = String(inspectionKey)
    const db    = await getDB()
    const tx    = db.transaction(PHOTOS_STORE, 'readwrite')
    const store = tx.objectStore(PHOTOS_STORE)
    const index = store.index('inspectionId')
    const all   = await idbRequest<StoredPhoto[]>(index.getAll(keyStr))

    for (const item of all) {
      if (!type || item.type === type) store.delete(item.key)
    }

    await txComplete(tx)
  },

  async hasPhotos(inspectionKey: string, type?: InspectionType): Promise<boolean> {
    const photos = await this.getPhotos(inspectionKey, type)
    return Object.keys(photos).length > 0
  },

  async countPhotos(inspectionKey: string, type?: InspectionType): Promise<number> {
    const photos = await this.getPhotos(inspectionKey, type)
    return Object.keys(photos).length
  },

  async countAllPhotos(): Promise<number> {
    try {
      const db = await getDB()
      const tx = db.transaction(PHOTOS_STORE, 'readonly')
      const st = tx.objectStore(PHOTOS_STORE)
      const [count] = await Promise.all([
        idbRequest<number>(st.count()),
        txComplete(tx),
      ])
      return count
    } catch {
      return 0
    }
  },

  async getAllGroups(): Promise<PhotoGroup[]> {
    try {
      const db = await getDB()
      const tx = db.transaction(PHOTOS_STORE, 'readonly')
      const st = tx.objectStore(PHOTOS_STORE)

      const groups = new Map<string, PhotoGroup>()

      await new Promise<void>((resolve, reject) => {
        const req = st.openCursor()

        req.onsuccess = (e) => {
          const cursor = (e.target as IDBRequest<IDBCursorWithValue>).result
          if (!cursor) { resolve(); return }

          const record   = cursor.value as StoredPhoto
          const groupKey = `${record.inspectionId}::${record.type}`

          if (groups.has(groupKey)) {
            groups.get(groupKey)!.count++
          } else {
            groups.set(groupKey, {
              contractId: record.inspectionId,
              type:       record.type,
              count:      1,
            })
          }

          cursor.continue()
        }

        req.onerror = () => reject(
          new Error(`[IndexedDB] getAllGroups cursor: ${req.error?.message}`)
        )
      })

      await txComplete(tx)
      return Array.from(groups.values())

    } catch (e) {
      console.warn('[offlinePhotoStore] getAllGroups:', (e as Error).message)
      return []
    }
  },

  // ── Drafts EDL ────────────────────────────────────────────────────────────
  // Logique migrée depuis useEdlDraftStore.ts (v4.0.0)
  // useEdlDraftStore devient un re-export de compatibilité

  /**
   * Sauvegarde un brouillon EDL complet dans IndexedDB.
   * Appelée par autoSaveDraft (debounce 300ms) ET saveImmediate.
   *
   * ✅ v5.0 — Accepte inspectionKey ("dep-CD25-04567") OU contractId + type.
   *
   * @param inspectionKeyOrContractId  inspectionKey ou contractId legacy
   * @param type                       'depart' | 'retour' (ignoré si inspectionKey)
   * @param payload                    Payload complet de l'inspection
   */
  async saveDraft(
    inspectionKeyOrContractId: string,
    type:       'depart' | 'retour',
    payload:    Record<string, unknown>
  ): Promise<void> {
    try {
      const db = await getDB()
      const draftKey = makeDraftKey(inspectionKeyOrContractId, type)
      // ✅ SÉCURITÉ SÉMANTIQUE — contractId = toujours l'ID MRC numérique (rawId/id)
      // Ne jamais stocker un inspectionKey dans contractId : les deux ont des formats différents
      const contract    = payload.contract as any
      const mrcId       = contract?.rawId ?? contract?.id ?? null
      const contractId  = mrcId ? String(mrcId) : inspectionKeyOrContractId
      const entry: EdlDraft = {
        key:        draftKey,
        contractId, // ← ID MRC numérique (ex: "703527"), JAMAIS un inspectionKey
        type,
        plate:      contract?.plate    ?? null,
        mileage:    (payload.mileage  as number)        ?? null,
        fuel:       (payload.fuel     as number)        ?? null,
        damages:    (payload.damages  as unknown[])     ?? [],
        contract:   payload.contract                    ?? {},
        startedAt:  (payload.startedAt as string)       ?? null,
        step:       (payload.step     as string)        ?? 'unknown',
        savedAt:    new Date().toISOString(),
        payload,    // payload complet pour saveImmediate
      }

      await new Promise<void>((resolve, reject) => {
        const tx  = db.transaction(DRAFTS_STORE, 'readwrite')
        const req = tx.objectStore(DRAFTS_STORE).put(entry)
        req.onsuccess = () => resolve()
        req.onerror   = () => reject(req.error)
      })

      console.log(`[offlinePhotoStore] ✅ Draft sauvegardé — ${draftKey}`)
    } catch (e) {
      console.warn('[offlinePhotoStore] saveDraft:', (e as Error).message)
    }
  },

  /**
   * Récupère un brouillon EDL depuis IndexedDB.
   * ✅ v5.0 — Accepte inspectionKey ou contractId + type.
   */
  async getDraft(
    inspectionKeyOrContractId: string,
    type:       'depart' | 'retour'
  ): Promise<EdlDraft | null> {
    try {
      const db  = await getDB()
      const key = makeDraftKey(inspectionKeyOrContractId, type)
      return await new Promise<EdlDraft | null>((resolve, reject) => {
        const tx  = db.transaction(DRAFTS_STORE, 'readonly')
        const req = tx.objectStore(DRAFTS_STORE).get(key)
        req.onsuccess = () => resolve((req.result as EdlDraft) ?? null)
        req.onerror   = () => reject(req.error)
      })
    } catch (e) {
      console.warn('[offlinePhotoStore] getDraft:', (e as Error).message)
      return null
    }
  },

  /**
   * Supprime un brouillon EDL depuis IndexedDB.
   * Appelée après upload réussi dans useUploadWorker.
   * ✅ v5.0 — Accepte inspectionKey ou contractId + type.
   */
  async clearDraft(
    inspectionKeyOrContractId: string,
    type:       'depart' | 'retour'
  ): Promise<void> {
    try {
      const db  = await getDB()
      const key = makeDraftKey(inspectionKeyOrContractId, type)
      await new Promise<void>((resolve, reject) => {
        const tx  = db.transaction(DRAFTS_STORE, 'readwrite')
        const req = tx.objectStore(DRAFTS_STORE).delete(key)
        req.onsuccess = () => resolve()
        req.onerror   = () => reject(req.error)
      })
    } catch (e) {
      console.warn('[offlinePhotoStore] clearDraft:', (e as Error).message)
    }
  },

  /**
   * Récupère tous les brouillons — utilisé par PendingQueueScreen.
   */
  async getAllDrafts(): Promise<EdlDraft[]> {
    try {
      const db = await getDB()
      return await new Promise<EdlDraft[]>((resolve, reject) => {
        const tx  = db.transaction(DRAFTS_STORE, 'readonly')
        const req = tx.objectStore(DRAFTS_STORE).getAll()
        req.onsuccess = () => resolve((req.result as EdlDraft[]) ?? [])
        req.onerror   = () => reject(req.error)
      })
    } catch (e) {
      console.warn('[offlinePhotoStore] getAllDrafts:', (e as Error).message)
      return []
    }
  },

  // ── Departure Cache (Phase 1 — Cache Offline) ────────────────────────────
  // Stocke les photos de départ dans IndexedDB pour les rendre disponibles
  // lors d'un retour hors connexion (ou sur une autre tablette après sync).

  /**
   * Sauvegarde les photos de départ d'un contrat dans le cache local.
   * Appelée après un téléchargement réussi depuis le serveur.
   *
   * @param contractId  ID du contrat
   * @param photos      Map zone → URL des photos de départ
   */
  async saveDepartureCache(
    contractId: string,
    photos:     Record<string, string>
  ): Promise<void> {
    if (!contractId || !photos || !Object.keys(photos).length) return
    const quotaInfo = await checkStorageQuota()
    if (quotaInfo.isNearLimit) {
      console.warn('[offlinePhotoStore] ⚠️ Stockage proche de la limite — cache départ non sauvegardé')
      return
    }
    const keyStr = String(contractId)
    try {
      const db = await getDB()
      await new Promise<void>((resolve, reject) => {
        const tx  = db.transaction(DEPARTURE_CACHE_STORE, 'readwrite')
        const req = tx.objectStore(DEPARTURE_CACHE_STORE).put({
          contractId: keyStr,
          photos,
          cachedAt: new Date().toISOString(),
        })
        req.onsuccess = () => resolve()
        req.onerror   = () => reject(req.error)
      })
      console.log(`[offlinePhotoStore] ✅ Cache départ sauvegardé — ${contractId} (${Object.keys(photos).length} photos)`)
    } catch (e) {
      console.warn('[offlinePhotoStore] saveDepartureCache:', (e as Error).message)
    }
  },

  /**
   * Récupère les photos de départ depuis le cache local.
   * Retourne null si le cache est absent ou expiré (> 7 jours).
   *
   * @param contractId  ID du contrat
   */
  async getDepartureCache(
    contractId: string
  ): Promise<Record<string, string> | null> {
    const keyStr = String(contractId)
    try {
      const db = await getDB()
      const result = await new Promise<{ contractId: string; photos: Record<string, string>; cachedAt: string } | undefined>((resolve, reject) => {
        const tx  = db.transaction(DEPARTURE_CACHE_STORE, 'readonly')
        const req = tx.objectStore(DEPARTURE_CACHE_STORE).get(keyStr)
        req.onsuccess = () => resolve(req.result)
        req.onerror   = () => reject(req.error)
      })
      if (!result?.photos) return null

      // Vérifier l'expiration du cache (7 jours)
      const cachedAt = new Date(result.cachedAt).getTime()
      if (Date.now() - cachedAt > DEPARTURE_CACHE_MAX_AGE_MS) {
        console.log(`[offlinePhotoStore] Cache départ expiré pour ${contractId} — suppression`)
        await this.clearDepartureCache(contractId)
        return null
      }

      return result.photos
    } catch (e) {
      console.warn('[offlinePhotoStore] getDepartureCache:', (e as Error).message)
      return null
    }
  },

  /**
   * Supprime le cache de photos de départ pour un contrat.
   * Appelée par useUploadWorker après upload réussi d'un retour.
   *
   * @param contractId  ID du contrat
   */
  async clearDepartureCache(contractId: string): Promise<void> {
    const keyStr = String(contractId)
    try {
      const db = await getDB()
      await new Promise<void>((resolve, reject) => {
        const tx  = db.transaction(DEPARTURE_CACHE_STORE, 'readwrite')
        const req = tx.objectStore(DEPARTURE_CACHE_STORE).delete(keyStr)
        req.onsuccess = () => resolve()
        req.onerror   = () => reject(req.error)
      })
      console.log(`[offlinePhotoStore] 🗑️ Cache départ supprimé — ${contractId}`)
    } catch (e) {
      console.warn('[offlinePhotoStore] clearDepartureCache:', (e as Error).message)
    }
  },

} as const